import { FC, ReactNode } from "react";

import Stack from "@mui/material/Stack";

import { AccordionRow } from "./CheckInAccordion/AccordionRow/AccordionRow";
import { CheckInAccordion } from "./CheckInAccordion/CheckInAccordion";
import { Header } from "./Header/Header";
import { CheckInAccordionKey, useCheckInToolsDrawer } from "./helpers/useCheckInToolsDrawer";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { CreditCard, Graph, Heart, Scan, Web } from "@/icons/outlined";

export interface CheckInToolsDrawerProps {
  open: boolean;
  onClose: () => void;
  installAppSlot?: ReactNode;
  collaboratorsSlot?: ReactNode;
  webCheckInAction?: ReactNode;
  printGuestListAction?: ReactNode;
  manageCollaboratorsView?: ReactNode;
  onBack?: () => void;
  onAccordionExpand?: (accordionKey: CheckInAccordionKey) => void;
}

export const CheckInToolsDrawer: FC<CheckInToolsDrawerProps> = ({
  open,
  onClose,
  installAppSlot,
  collaboratorsSlot,
  webCheckInAction,
  printGuestListAction,
  manageCollaboratorsView,
  onBack,
  onAccordionExpand,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const { isTapToPaySupported, openAccordions, toggleAccordion } = useCheckInToolsDrawer({ onAccordionExpand });
  const contentPadding = isSmallScreen ? { paddingX: 3, paddingBottom: 5 } : { padding: 3 };

  return (
    <DashboardDrawer
      open={open}
      onClose={onClose}
      anchor={isSmallScreen ? "bottom" : "right"}
      size="small"
      noPadding
      noDivider
      data-test="check-in-tools-drawer"
      sx={(theme) => ({ zIndex: theme.zIndex.modal + 1 })}
      PaperProps={{ sx: (theme) => ({ boxShadow: theme.boxShadow.base.strong }) }}
      slots={{
        header: manageCollaboratorsView ? (
          <Header
            title={t("dashboard", "checkInToolsDrawer.manageCollaborators.title")}
            onClose={onClose}
            onBack={onBack}
            isMobile={isSmallScreen}
          />
        ) : (
          <Header title={t("dashboard", "checkInToolsDrawer.title")} onClose={onClose} isMobile={isSmallScreen} />
        ),
      }}
    >
      {manageCollaboratorsView ? (
        <Stack sx={contentPadding}>{manageCollaboratorsView}</Stack>
      ) : (
        <Stack gap={1.5} sx={contentPadding}>
          <CheckInAccordion
            title={t("dashboard", "checkInToolsDrawer.zeffyApp.title")}
            chipLabel={t("dashboard", "checkInToolsDrawer.recommended")}
            isOpen={openAccordions.zeffyApp}
            onToggle={() => toggleAccordion("zeffyApp")}
            isMobile={isSmallScreen}
            rowGap={1.5}
            data-test="check-in-accordion-zeffy-app"
          >
            <AccordionRow icon={<Scan />}>{t("dashboard", "checkInToolsDrawer.zeffyApp.fastestCheckIn")}</AccordionRow>
            {isTapToPaySupported && (
              <AccordionRow icon={<CreditCard />} data-test="check-in-tap-to-pay-row">
                {t("dashboard", "checkInToolsDrawer.zeffyApp.tapToPay")}
              </AccordionRow>
            )}
            <AccordionRow icon={<Graph />}>
              {t("dashboard", "checkInToolsDrawer.zeffyApp.realTimeTracking")}
            </AccordionRow>
            {(installAppSlot || collaboratorsSlot) && (
              <Stack gap={1} sx={{ paddingTop: 0.5 }}>
                {installAppSlot}
                {collaboratorsSlot}
              </Stack>
            )}
          </CheckInAccordion>

          <CheckInAccordion
            title={t("dashboard", "checkInToolsDrawer.webCheckIn.title")}
            isOpen={openAccordions.webCheckIn}
            onToggle={() => toggleAccordion("webCheckIn")}
            isMobile={isSmallScreen}
            rowGap={1}
            action={webCheckInAction}
            data-test="check-in-accordion-web-check-in"
          >
            <AccordionRow icon={<Web />} iconAlignment="top">
              <Trans space="dashboard" i18nKey="checkInToolsDrawer.webCheckIn.manager" components={BOLD_COMPONENTS} />
            </AccordionRow>
            <AccordionRow icon={<Scan />} iconAlignment="top">
              <Trans space="dashboard" i18nKey="checkInToolsDrawer.webCheckIn.scan" components={BOLD_COMPONENTS} />
            </AccordionRow>
            <AccordionRow icon={<Heart />} iconAlignment="top">
              {t("dashboard", "checkInToolsDrawer.webCheckIn.smallerGuestLists")}
            </AccordionRow>
          </CheckInAccordion>

          <CheckInAccordion
            title={t("dashboard", "checkInToolsDrawer.printGuestList.title")}
            isOpen={openAccordions.printGuestList}
            onToggle={() => toggleAccordion("printGuestList")}
            isMobile={isSmallScreen}
            rowGap={1}
            contentGap={1.5}
            action={printGuestListAction}
            data-test="check-in-accordion-print-guest-list"
          >
            <AccordionRow>{t("dashboard", "checkInToolsDrawer.printGuestList.printableList")}</AccordionRow>
            <AccordionRow>{t("dashboard", "checkInToolsDrawer.printGuestList.unreliableInternet")}</AccordionRow>
          </CheckInAccordion>
        </Stack>
      )}
    </DashboardDrawer>
  );
};

const BOLD_COMPONENTS = { bold: <strong /> };
